import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Redirect,
	Route,
	Switch,
	useLocation,
	useParams
} from "react-router-dom";
import Socket, { SocketMessage } from "../../connection/socket";
import { updateUserSessionEndTime, updateEventBundle, loadPage, loadEventBundle, setBlProfileToken, getTicketQuantities } from "../../store/actions/event/event-actions";
import { EventsState } from "../../store/types";
import { Dictionary, LanguagesAbbr } from "../../types/working-model";
import WaitingIndicator from "../general-ui/waiting-indicator/waiting-indicator";
import LiveEventDocumentHead from "./live-event-document-head";
import MarketingPage from "./marketing-page/marketing-page";
import MarketingRegisteredRoutes from "./marketing-page/marketing-registered-routes";
import PrivacyPolicy from "./privacy-policy/privacy-policy";
import RegistrationPage from "./registration/registration";
import NonRegisteredUserForm from "./session/live-chat/non-registered-user-form";
import Session from "./session/session";
import TermsConditions from "./terms-and-conditions/terms-and-conditions";
import { eventPath } from "./utils";
import ScrollToTop from './scrolll-to-top';
import { getStorageItem } from "../../utils/local-storage";
import { trackGoogleAnalytics } from "../../utils/google-analytics";
import classNames from 'classnames';
import { useRef } from "react";

export interface ParamsProps {
	id: string; //event uuid
	uuid: string;
	eventName: string;
	tab: string; //editor nav tab
	language: LanguagesAbbr;
	sessionUuid: string;
}

export interface LiveEventProps {
	debug?: boolean;
	host?: string;
	eventNamePass?: string;
}

function Loader() {
	return (
		<div style={{
			position: 'absolute',
			inset: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'white'
		}}>
			<WaitingIndicator />
		</div>
	);
}

export function regisrationPath(lang: string, eventName?: string, uuid?: string): string {
	if (eventName) return `${eventName}/${lang}/registration`;
	else return `event/${uuid}/${lang}/registration`;
}

const LiveEvent: React.FC<LiveEventProps> = ({ debug, host, eventNamePass }) => {
	const dispatch = useDispatch();

	const { uuid }: ParamsProps = useParams();

	const { pathname } = useLocation();

	const {
		LiveEventReducer: {
			eventBundle,
			validRegistration,
			registrationId,
			loadingEventBundle,
			userSession,
			_404,
			shouldRedirectToRegistration,
			paymentRequired
		},
	} = useSelector((event: EventsState) => event);

	const [canShowLoader, setCanShowLoader] = useState(true);
	const socket = useRef<Socket | null>(null);

	// if storage token then set it in redux
	useEffect(() => {
		const blProfileToken = getStorageItem('Bl-Profile-Token');
		if (blProfileToken) {
			dispatch(setBlProfileToken(blProfileToken));
		}

		trackGoogleAnalytics();
	}, [dispatch]);

	useEffect(() => {
		if (eventBundle) return;

		//REMOVE THIS BLOCK WHEN REMOVING DEPRECATED METHOD
		if (!debug) {
			if (eventNamePass) {
				//munging for staging
				dispatch(loadEventBundle(host!.replace('brandlive-staging.com', 'brand.live'), eventNamePass));
				dispatch(getTicketQuantities(host!.replace('brandlive-staging.com', 'brand.live'), eventNamePass));
			}
		} else {
			dispatch(loadEventBundle('dev', uuid!));
			dispatch(getTicketQuantities('dev', uuid!));
		}

	}, [uuid, dispatch, debug, host, eventNamePass, eventBundle]);

	useEffect(() => {
		if (eventBundle && loadingEventBundle) {
			dispatch(loadPage(eventBundle));
		}
	}, [dispatch, eventBundle, loadingEventBundle]);

	const getLanguage = () => {
		if (eventBundle?.homepage) {
			const lang = window.navigator.language.substr(0, 2);
			if (eventBundle.homepage.languages.includes(lang)) {
				return lang;
			} else {
				return eventBundle.homepage.languages[0];
			}
		} else {
			if (eventBundle?.sessions?.[0]) {
				const lang = window.navigator.language.substr(0, 2);

				if (eventBundle.sessions[0].languages.includes(lang)) {
					return lang;
				} else {
					return eventBundle.sessions[0].default_language;
				}
			} else {
				return 'en';
			}
		}
	};

	useEffect(() => {
		if (!userSession) {
			return;
		}

		const updateEndTime = () => {
			dispatch(updateUserSessionEndTime(userSession));
		};

		const updateEndTimeVisibility = () => {
			const { visibilityState } = document;

			if (visibilityState != 'hidden') {
				return;
			}

			updateEndTime();
		};

		// Not all browsers support visibility change. And some, like safari, just don't fire it when leaving the page.
		// pagehide and (before)unload are less reliable but a good fallback
		document.addEventListener('visibilitychange', updateEndTimeVisibility);

		if (typeof window.onpagehide != undefined) {
			window.addEventListener('pagehide', updateEndTime);
		} else {
			window.addEventListener('unload', updateEndTime);
			window.addEventListener('beforeunload', updateEndTime);
		}


		return () => {
			dispatch(updateUserSessionEndTime(userSession));
			window.removeEventListener('pagehide', updateEndTime);
			window.removeEventListener('unload', updateEndTime);
			window.removeEventListener('beforeunload', updateEndTime);
			document.removeEventListener('visibilitychange', updateEndTimeVisibility);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userSession]);

	useEffect(() => {
		const handleBundleUpdate = (message: SocketMessage) => {
			dispatch(updateEventBundle(message.data));
		};

		const handleSecBundleUpdate = (message: SocketMessage) => {
			dispatch(updateEventBundle(message.data));
		};

		//create manually - with useSocket we were getting channels named `event-undefined`
		if (eventBundle?.uuid) {
			socket.current = new Socket(`event-${eventBundle.uuid}`);
			if (registrationId && validRegistration) {
				socket.current.addListener('bundle_update_sec', handleSecBundleUpdate);
			} else {
				socket.current.addListener('bundle_update', handleBundleUpdate);
			}
		}

		return () => {
			//abandon this socket, we'll get a new one on the next render
			socket.current?.disconnect();
			socket.current?.removeListener('bundle_update', handleBundleUpdate);
			socket.current?.removeListener('bundle_update_sec', handleSecBundleUpdate);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket, uuid, registrationId, validRegistration]);

	const RouteMap = useMemo(() => {
		let routeMap: Dictionary = {};

		if (!debug) {
			routeMap = {
				Session: "/:eventName/:language/session/:sessionUuid",
				Registration: "/:eventName/:language/registration/:sessionUuid?",
				PrivacyPolicy: "/:eventName/:language/privacy-policy",
				TermsConditions: "/:eventName/:language/terms-conditions",
				Homepage: "/:eventName/:language/home",
				Sessions: "/:eventName/:language/sessions",
				Landing: "/:eventName/:language",
				LandingRedirect: "/:eventName"
			};
		} else {
			routeMap = {
				Session: "/event/:uuid/:language/session/:sessionUuid",
				Registration: "/event/:uuid/:language/registration",
				PrivacyPolicy: "/event/:uuid/:language/privacy-policy",
				TermsConditions: "/event/:uuid/:language/terms-conditions",
				Homepage: "/event/:uuid/:language/home",
				Sessions: "/event/:uuid/:language/sessions",
				Landing: "/event/:uuid/:language",
				LandingRedirect: "/event/:uuid"
			};
		}

		return routeMap;
	}, [debug]);

	useEffect(() => {
		if (eventBundle?.channel_settings?.integrations?.google_analytics && eventBundle.channel_settings.integrations.google_analytics.startsWith("UA-")) {
			window.ga("create", eventBundle.channel_settings.integrations.google_analytics, "auto", `brandlive${eventBundle.uuid}`);
			window.ga(`brandlive${eventBundle.uuid}.send`, "pageview");
		}
	}, [eventBundle?.channel_settings?.integrations?.google_analytics, eventBundle?.uuid]);

	const fadeOutLoader = useCallback(() => {
		setCanShowLoader(false);
	}, []);

	// use event favicon if exists, else use channel favicon, else nothing
	const favIcon = useCallback(() => {
		return eventBundle?.favicon || eventBundle?.channel_logos?.favicon || undefined;
	}, [eventBundle]);

	if (_404) {
		return (
			<div className="_404">
				<h1>404</h1>
				<p>Not Found</p>
			</div>
		);
	}

	if (!eventBundle) {
		return (
			<Loader />
		);
	}

	const hasHomepage = !!eventBundle.homepage;

	return hasHomepage ? (
		<div className="live-event-container">
			<LiveEventDocumentHead eventBundle={eventBundle} favIcon={favIcon()} />
			<ScrollToTop />
			<Switch>
				<Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} /> {/* Removes trailing slashes */}
				{(shouldRedirectToRegistration || paymentRequired) && (
					<Route path={[RouteMap.Homepage, RouteMap.Landing, RouteMap.Session]} exact={true}>
						<Redirect to={{
							pathname: `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/registration`,
							state: { referrer: window.location.pathname }
						}} />
					</Route>
				)}
				<Route path={RouteMap.Session}>
					{(eventBundle.registration_on ? (
						(registrationId && validRegistration) ?
							<Session /> :
							<Loader />
					) :
						<Session />
					)}
				</Route>
				<Route path={RouteMap.Registration}>
					<RegistrationPage />
				</Route>
				<Route path={RouteMap.PrivacyPolicy}>
					<PrivacyPolicy />
				</Route>
				<Route path={RouteMap.TermsConditions}>
					<TermsConditions />
				</Route>
				<Route path={[RouteMap.Homepage, RouteMap.Sessions]}>
					{eventBundle.registration_on ? ((registrationId && validRegistration) ? <MarketingRegisteredRoutes debug={debug} /> : <MarketingPage />) : (<MarketingRegisteredRoutes debug={debug} />)}
				</Route>
				<Route path={RouteMap.Landing}>
					{(registrationId) ? <MarketingRegisteredRoutes debug={debug} /> : <MarketingPage />}
				</Route>

				<Route path={RouteMap.LandingRedirect}>
					<Redirect to={`/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />
				</Route>
			</Switch>

			<NonRegisteredUserForm
				event_uuid={eventBundle.uuid}
				event={eventBundle.event!}
				channel={eventBundle.channel}
			/>

			{canShowLoader && (
				<div
					className={classNames('live-event-waiting', { 'fade-out': !loadingEventBundle })}
					onTransitionEnd={fadeOutLoader}
				>
					<WaitingIndicator />
				</div>
			)}
		</div>
	) : (
		<div className="live-event-container">
			<LiveEventDocumentHead eventBundle={eventBundle} favIcon={favIcon()} />
			<ScrollToTop />
			{eventBundle.registration_on ? (
				<Switch>
					<Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} /> {/* Removes trailing slashes */}
					{(shouldRedirectToRegistration || paymentRequired) && (
						<Route path={[RouteMap.Landing]} exact={true}>
							<Redirect to={`/${regisrationPath(getLanguage(), eventNamePass, uuid)}`} />
						</Route>
					)}
					<Route path={RouteMap.Registration}>
						<RegistrationPage />
					</Route>
					<Route path={RouteMap.Landing}>
						{loadingEventBundle ? (
							<></>
						) : (
							<>
								{(registrationId) ? (
									<Session singleSession={true} />
								) : (
									<>
										<Redirect to={`/${regisrationPath(getLanguage(), eventNamePass, uuid)}`} />
									</>
								)}
							</>
						)}
					</Route>

					<Route path={RouteMap.LandingRedirect}>
						<Redirect to={`/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />
					</Route>
					<Route path={RouteMap.PrivacyPolicy}>
						<PrivacyPolicy />
					</Route>
					<Route path={RouteMap.TermsConditions}>
						<TermsConditions />
					</Route>
				</Switch>
			) : (
				<Switch>
					<Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} /> {/* Removes trailing slashes */}
					<Route path={RouteMap.Landing}>
						<Session singleSession={true} />
					</Route>
					<Route path={RouteMap.LandingRedirect}>
						<Redirect to={`/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />
					</Route>
					<Route path={RouteMap.PrivacyPolicy}>
						<PrivacyPolicy />
					</Route>
					<Route path={RouteMap.TermsConditions}>
						<TermsConditions />
					</Route>
				</Switch>
			)}

			<NonRegisteredUserForm
				event_uuid={eventBundle.uuid}
				event={eventBundle.event!}
				channel={eventBundle.channel}
			/>

			{canShowLoader && (
				<div
					className={classNames('live-event-waiting', { 'fade-out': !loadingEventBundle })}
					onTransitionEnd={fadeOutLoader}
				>
					<WaitingIndicator />
				</div>
			)}
		</div>
	);
};

export default LiveEvent;